import {
  Button,
  Form,
  Input,
  Select,
  Spin,
  notification,
  Card,
  Divider,
} from "antd";
import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserOutlined,
  MedicineBoxOutlined,
  ArrowLeftOutlined,
  SendOutlined,
} from "@ant-design/icons";
import Empty from "../../components/elements/empty";
import Colors from "../../constants/Colors";
import Medication from "../../components/card/medication";
import AddMedicationModal, {
  type MedicationFormValues,
} from "../../components/prescriptions/add-medication-modal";
import DataProvider from "../../apis";
import { useAppStore } from "../../store";
import { E_PRESCRIPTIONS_BASE } from "../../constants/e-prescriptions";
import { REGISTRATION_PLATFORM_DOCTOR_WEBSITE } from "../../constants/registrationPlatform";

// Insurance providers are `Partner` rows with type "insurance" on the
// backend. Query mirrors the admin panel's already-working lookup
// (admin/src/components/prescription-user.tsx) — `filter=type||eq||insurance`
// is the nestjsx/crud filter syntax, not a REST path param.
interface InsuranceProviderOption {
  id: number;
  name: string;
}

type FieldType = MedicationFormValues;

type PatientInfoType = {
  patientName?: string;
  patientEmail?: string;
  contact?: string;
  location?: string;
  notes?: string;
  institution?: string;
  doctorContact?: string;
  image?: string;
  insuranceProvider?: string;
  policyNumber?: string;
};

export default function Createeprescription() {
  const [form] = Form.useForm();
  const [medicationList, setMedicationList] = useState<FieldType[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user = useAppStore((state) => state.user);

  const [insuranceOptions, setInsuranceOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [insuranceOptionsLoading, setInsuranceOptionsLoading] = useState(true);
  const [insuranceOptionsError, setInsuranceOptionsError] = useState(false);

  const loadInsuranceOptions = useCallback(() => {
    setInsuranceOptionsLoading(true);
    setInsuranceOptionsError(false);
    DataProvider.getPartners("filter=type||eq||insurance&limit=200")
      .then((res) => {
        const raw = res.data;
        const list: InsuranceProviderOption[] = Array.isArray(raw)
          ? raw
          : (raw as { data?: InsuranceProviderOption[] })?.data ?? [];
        setInsuranceOptions(
          list.map((p) => ({ label: p.name, value: p.name }))
        );
      })
      .catch(() => setInsuranceOptionsError(true))
      .finally(() => setInsuranceOptionsLoading(false));
  }, []);

  useEffect(() => {
    loadInsuranceOptions();
  }, [loadInsuranceOptions]);

  const handleDeleteMedication = (medicationToDelete: FieldType) => {
    setMedicationList((prev) => prev.filter((m) => m !== medicationToDelete));
  };

  const showModal = () => setOpen(true);
  const hideModal = () => setOpen(false);

  const addMedication = (values: MedicationFormValues) => {
    setMedicationList((prev) => [...prev, values]);
    hideModal();
  };

  const handleSubmit = () => {
    form.validateFields().then((patientInfo: PatientInfoType) => {
      setLoading(true);
      const payload = {
        patientName: patientInfo.patientName!,
        patientEmail: patientInfo.patientEmail || undefined,
        patientContact: patientInfo.contact!,
        patientLocation: patientInfo.location || undefined,
        notes: patientInfo.notes?.trim() || undefined,
        institution: patientInfo.institution || undefined,
        doctorContact: patientInfo.doctorContact ?? user?.email ?? undefined,
        image: patientInfo.image || undefined,
        insuranceProvider: patientInfo.insuranceProvider || undefined,
        policyNumber: patientInfo.policyNumber || undefined,
        // Medications are sent structured (matches new-doctor-app) — the
        // backend saves each entry as a real OrderDetail row
        // (doctorPrescription.doctorOrderDetails), which the pharmacist
        // queue's medication-availability tracking reads from. Flattening
        // these into `notes` as text (the old behaviour) left website-created
        // prescriptions with zero OrderDetail rows — invisible to that flow.
        //
        // `medicine` is guaranteed non-empty here — the "Add medication"
        // modal below requires it before an entry ever lands in the list —
        // but FieldType itself marks it optional, so filter+assert to
        // satisfy the stricter payload type.
        medications:
          medicationList.length > 0
            ? medicationList
                .filter((m): m is FieldType & { medicine: string } => !!m.medicine)
                .map((m) => ({
                  medicine: m.medicine,
                  dose: m.dose || undefined,
                  route: m.route || undefined,
                  dosage: m.dosage || undefined,
                  period: m.period || undefined,
                }))
            : undefined,
        intakeMode: (medicationList.length > 0
          ? "catalog_request"
          : "upload") as "catalog_request" | "upload",
        source: REGISTRATION_PLATFORM_DOCTOR_WEBSITE,
      };

      DataProvider.createDoctorPrescription(payload)
        .then(() => {
          notification.success({
            message: "Prescription submitted",
            description:
              "Our team will process this order and arrange delivery to your patient.",
          });
          navigate(E_PRESCRIPTIONS_BASE);
        })
        .catch((err) => {
          const msg =
            err?.response?.data?.message ||
            "Something went wrong. Please try again.";
          notification.error({ message: "Error", description: msg });
        })
        .finally(() => setLoading(false));
    });
  };

  return (
    <div className="min-h-[70vh] bg-[#f8fafc] pb-12">
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(E_PRESCRIPTIONS_BASE)}
          className="mb-2 -ml-1 text-gray-600 hover:text-gray-900"
          aria-label="Back to e-prescriptions"
        >
          Back
        </Button>

        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 m-0">
            New e-prescription
          </h1>
          <p className="text-gray-600 mt-1 mb-0 text-sm">
            Enter patient and delivery details. We’ll fulfill the order and
            deliver to your patient (no MedPharma account required for them).
          </p>
        </div>

        <div className="space-y-6">
          {/* Section 1: Patient & delivery */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <UserOutlined className="text-gray-500" />
                Patient & delivery
              </span>
            }
            className="rounded-xl shadow-sm border-gray-100"
            styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
          >
            <Form layout="vertical" form={form} requiredMark={false}>
              <Form.Item<PatientInfoType>
                label="Patient name"
                name="patientName"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="Full name of patient" size="large" />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Patient phone (for delivery contact)"
                name="contact"
                rules={[
                  { required: true, message: "Required for delivery" },
                  { min: 10, message: "At least 10 characters" },
                ]}
              >
                <Input placeholder="e.g. 0509123456" size="large" />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Delivery address / location"
                name="location"
              >
                <Input.TextArea
                  placeholder="Address where medications should be delivered"
                  rows={2}
                />
              </Form.Item>
              <Form.Item<PatientInfoType> label="Patient email (optional)" name="patientEmail">
                <Input type="email" placeholder="patient@example.com" />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Your facility name (optional)"
                name="institution"
              >
                <Input placeholder="Clinic or hospital name" />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Your contact for this order (optional)"
                name="doctorContact"
              >
                <Input placeholder="Phone or email for follow-up" />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Prescription image URL (optional)"
                name="image"
              >
                <Input placeholder="https://..." />
              </Form.Item>
              <Form.Item<PatientInfoType> label="Clinical notes (optional)" name="notes">
                <Input.TextArea placeholder="Instructions, allergies, etc." rows={3} />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Insurance provider (optional)"
                name="insuranceProvider"
                extra={
                  insuranceOptionsError ? (
                    <span className="text-red-500 text-xs">
                      Could not load providers.{" "}
                      <button
                        type="button"
                        onClick={loadInsuranceOptions}
                        className="text-red-500 underline"
                      >
                        Try again
                      </button>
                    </span>
                  ) : undefined
                }
              >
                <Select
                  placeholder="Select insurance provider"
                  options={insuranceOptions}
                  loading={insuranceOptionsLoading}
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  notFoundContent={
                    insuranceOptionsLoading ? <Spin size="small" /> : "No providers found"
                  }
                />
              </Form.Item>
              <Form.Item<PatientInfoType>
                label="Policy number (optional)"
                name="policyNumber"
              >
                <Input placeholder="e.g. NHIS-0012345" />
              </Form.Item>
            </Form>
          </Card>

          {/* Section 2: Medications */}
          <Card
            title={
              <span className="flex items-center gap-2">
                <MedicineBoxOutlined className="text-gray-500" />
                Medications
              </span>
            }
            className="rounded-xl shadow-sm border-gray-100"
            styles={{ header: { borderBottom: "1px solid #f0f0f0" } }}
          >
            {medicationList.length === 0 ? (
              <Empty
                imageStyle="md:h-36 h-24"
                titleStyle="text-base"
                desStyle="text-sm"
                title="No medications added"
                buttons={[{ name: "Add medication", event: showModal }]}
                description="Add at least one medication for this prescription."
              />
            ) : (
              <div className="space-y-3">
                <Button
                  type="primary"
                  onClick={showModal}
                  style={{ background: Colors.primary }}
                  aria-label="Add another medication"
                >
                  Add medication
                </Button>
                {medicationList.map((medication, index) => (
                  <Medication
                    key={index}
                    medication={medication}
                    onDelete={handleDeleteMedication}
                  />
                ))}
              </div>
            )}
          </Card>

          <Divider className="my-6" />

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button
              size="large"
              onClick={() => navigate(E_PRESCRIPTIONS_BASE)}
              aria-label="Cancel and go back"
            >
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={loading ? <Spin size="small" /> : <SendOutlined />}
              onClick={handleSubmit}
              disabled={loading || medicationList.length === 0}
              aria-busy={loading}
              aria-label="Submit prescription to MedPharma"
              style={{ background: Colors.primary, paddingLeft: 24, paddingRight: 24 }}
            >
              {loading ? "Submitting…" : "Submit to MedPharma"}
            </Button>
          </div>
        </div>
      </div>

      <AddMedicationModal open={open} onCancel={hideModal} onSubmit={addMedication} />
    </div>
  );
}
